import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { RecoveryCodePurpose, TwoFactorVerificationResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, Subscription, timer } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { SharedModule } from '../../../../../shared/shared.module';
import { E2eIdDirective } from '../../../../../v2-layout/shared/directives/e2e-id.directive';
import { NgxMaskDirective } from 'ngx-mask';
import { CopyConfirmState, CopyToClipboardDirective } from '../../../../../v2-layout/shared/directives/button.directive';

/**
 * The recovery-code entry mask (SD-11060). New codes are 13 characters shown 5-4-4; codes issued before that
 * are 10 characters shown 5-5 and stay valid, so both shapes must fit. ngx-mask uses the first pattern until
 * the entry outgrows it, then reformats to the second.
 */
export const RECOVERY_CODE_MASK = 'AAAAA-AAAAA||AAAAA-AAAA-AAAA';

export interface ConfirmTwoFactorInput
{
    type: 'google' | 'sms' | null;

    /**
     * Whose 2FA to challenge. Optional: the login/reset gates pass it explicitly (the user is not
     * authenticated yet), while the Settings paths omit it and the UserSettingsService falls back to
     * the authenticated user. Declared required until SD-8082, but every Settings call site omitted
     * it behind a `<ConfirmTwoFactorInput>` cast — which is what hid the mismatch.
     */
    userId?: number;
    canGoBack?: boolean;
    showTrustDevice?: boolean;

    /**
     * Whether the challenged user actually holds a redeemable recovery code (SD-8982). Drives
     * whether the "Use a recovery code instead" fallback is offered — without it the link is a dead
     * end for the large population that is 2FA-enrolled with no codes, who reach a code entry they
     * cannot satisfy and get "Recovery code could not be validated".
     *
     * TRI-STATE, and the distinction matters: `false` means "known to have none" → hide the link.
     * `undefined` means "not known here" → SHOW it, preserving existing behaviour. Only the
     * post-auth Settings call sites know the answer; the pre-auth gates (login, password reset,
     * payment login) do not, and must not be made to guess — whether a pre-auth surface may reveal
     * that an account holds recovery codes is an information-disclosure question tracked on SD-8956,
     * not something to settle with a defaulted boolean here.
     */
    hasRecoveryCodes?: boolean;

    /**
     * What a redeemed recovery code is for (SD-11060). Sent to the server, which skips the replacement
     * code when the set is about to go (Settings regenerate, or disabling the last factor). Omitted by the
     * login and reset gates, which get the default: a replacement.
     */
    recoveryPurpose?: RecoveryCodePurpose;
}

export interface ConfirmTwoFactorOutput
{
    success: boolean;
    type?: 'google' | 'sms' | 'recovery';
    token?: string;
    trustDevice?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-confirm-2fa-code',
    templateUrl: './confirm-2fa-code.component.html',
    standalone: true,
    imports: [
        E2eIdDirective,
        NgxMaskDirective,
        CopyToClipboardDirective,
        SharedModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Confirm2faCodeComponent implements OnChanges, OnDestroy
{
    @Input() open = false;
    @Input() data: ConfirmTwoFactorInput = {type: null};
    @Input() disableClose = false;
    @Output() closed = new EventEmitter<ConfirmTwoFactorOutput | null>();

    @ViewChild('digits') private _digits: ElementRef<HTMLElement>;

    isSubmitting = false;
    isSuccess = false;
    type: 'google' | 'sms' | 'recovery' | null = null;
    canGoBack = true;

    /** Free-text entry for a recovery (backup) code — distinct from the 6-digit OTP (SD-8082). */
    recoveryCode = '';
    readonly recoveryCodeMask = RECOVERY_CODE_MASK;

    /**
     * SD-11060: the code the server minted to replace the one just redeemed. While set, the dialog shows
     * it once (the enrollment "store this now" panel) and holds the verification result until the user
     * confirms they saved it, so the replacement is never skipped past.
     */
    replacementCode: string | null = null;
    replacementSaved = false;
    replacementCopyState: CopyConfirmState = null;
    private _pendingRecoveryResult: ConfirmTwoFactorOutput | null = null;

    /** The method the user switched away from when choosing "use a recovery code", so Back returns to it. */
    private _previousType: 'google' | 'sms' | null = null;
    isSendingSms = false;
    isResending = false;
    showTrustDevice = false;
    trustDevice = false;
    hasError = false;

    /** Current entry from the OTP control. */
    code = '';

    /**
     * Whether to offer the recovery-code fallback (SD-8982). Hidden ONLY when the caller states the
     * user has no codes; an absent flag keeps the link, so the pre-auth gates are unchanged.
     */
    canUseRecoveryCode = true;

    /** Seconds left before the sent SMS code is treated as expired (drives the countdown). */
    secondsRemaining = 0;
    isExpired = false;

    /** True when an SMS send/resend failed — surfaces the resend affordance so a failed send
     *  isn't a dead-end (the modal can open directly in SMS mode, so there's no method screen to
     *  fall back to). */
    smsSendFailed = false;

    /**
     * How long a sent SMS code stays valid. The code lives on Twilio Verify (default 10 min),
     * not in PayHOA, and the send endpoint returns no expiry — so this is a client-side
     * approximation of that window. The server remains the source of truth: a truly-expired
     * code still fails validation regardless of this countdown.
     */
    private readonly _codeValiditySeconds = 600;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;
    private _countdownSub: Subscription | null = null;
    private _session$ = new Subject<void>();

    constructor(
        private _userSettingsService: UserSettingsService,
        private _growler: GrowlerService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['open']) {
            return;
        }
        if (this.open && !changes['open'].previousValue) {
            this.start();
        } else if (!this.open && changes['open'].previousValue) {
            this.teardown();
        }
    }

    onMethodSelect(value: string): void {
        if (value === 'sms') {
            this.setSmsType();
        } else if (value === 'google') {
            this.setGoogleType();
        }
    }

    cancel(): void {
        this.closed.emit(null);
    }

    onDialogClose(): void {
        // The replacement step cannot be dismissed (SD-11060): the code was already redeemed, so closing
        // would both throw away the new code and abandon the sign-in it unlocked.
        if (!this.disableClose && !this.replacementCode) {
            this.cancel();
        }
    }

    setSmsType() {
        this.isSendingSms = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId)
            .pipe(takeUntil(this._session$), untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isSendingSms = false;
                    this.type = 'sms';
                    this.startCountdown();
                    setTimeout(() => this.focusDigits());
                },
                error: () => {
                    this._growler.error('Error', 'We encountered an error while trying to send the code.');
                    this.isSendingSms = false;
                    this.smsSendFailed = true;
                },
            });
    }

    setGoogleType() {
        this.type = 'google';
        setTimeout(() => this.focusDigits());
    }

    /** Switch from OTP entry to recovery-code entry, remembering the method to return to. */
    setRecoveryType() {
        this._previousType = this.type as 'google' | 'sms';
        this.stopCountdown();
        this.type = 'recovery';
        this.recoveryCode = '';
        this.hasError = false;
    }

    /** Return from recovery-code entry to the method the user came from. */
    backFromRecovery() {
        this.type = this._previousType;
        this.recoveryCode = '';
        this.hasError = false;
    }

    /** Re-send the SMS code to the enrolled number and restart the validity countdown. */
    resendCode() {
        if (this.isResending) {
            return;
        }

        this.isResending = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId)
            .pipe(takeUntil(this._session$), untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isResending = false;
                    this.code = '';
                    this.startCountdown();
                    setTimeout(() => this.focusDigits());
                    this._growler.success('Code sent', 'We\'ve sent a new code to your phone.');
                },
                error: () => {
                    this.isResending = false;
                    this.smsSendFailed = true;
                    this._growler.error('Error', 'We couldn\'t resend the code. Please try again.');
                },
            });
    }

    /** Return to the type-selection screen and clear any partial entry. */
    goBack() {
        this.stopCountdown();
        this.type = null;
        this.code = '';
    }

    /** The dialog heading; the replacement step names what it is asking for (SD-11060). */
    get heading(): string {
        return this.replacementCode ? 'Save your new recovery code' : 'Additional Verification Required';
    }

    get subtitle(): string {
        if (this.replacementCode) {
            return 'Here is a new code to replace the one you just used.';
        }
        if (!this.type) {
            return 'Please select how you would like to verify your identity';
        }
        if (this.type === 'recovery') {
            return 'Enter one of your saved recovery codes';
        }
        if (this.type === 'sms') {
            return 'Enter the 6-digit code sent to your device';
        }
        return 'Open your authenticator app to get your code';
    }

    /** Hint line for the recovery-code entry screen (mirrors the OTP hintText). */
    get recoveryHint(): string {
        if (this.hasError) {
            return 'That code was not accepted — please try another';
        }
        if (this.isSuccess) {
            // Deliberately neutral: this screen is also reached from Settings → regenerate
            // recovery codes, where the user is re-authenticating rather than logging in.
            return 'Verified!';
        }
        if (this.isSubmitting) {
            return 'Verifying...';
        }
        return 'Enter a backup code (for example, ABCDE-2345-6789)';
    }

    get hintText(): string {
        if (this.hasError) {
            return 'Incorrect code — please try again';
        }
        if (this.isSuccess) {
            // Neutral for the same reason as recoveryHint: this screen is opened for password
            // reset and from Settings (disable a method, regenerate recovery codes), none of
            // which are a login.
            return 'Verified!';
        }
        if (this.isSubmitting) {
            return 'Verifying...';
        }
        return `${this.code.length} of 6 digits entered`;
    }

    get verifyLabel(): string {
        if (this.isSuccess) {
            return 'Verified';
        }
        return this.isSubmitting ? 'Verifying…' : 'Verify';
    }

    /** Whether to show the countdown / resend row — SMS only, once a send has been attempted
     *  (a running countdown, an expired code, or a failed send all offer resend). */
    get showResendRow(): boolean {
        return this.type === 'sms' && (this.secondsRemaining > 0 || this.isExpired || this.smsSendFailed);
    }

    /** Status line shown alongside the resend button on the SMS screen. */
    get resendStatus(): string {
        if (this.smsSendFailed) {
            return 'We couldn\'t send a code';
        }
        if (this.isExpired) {
            return 'Code expired';
        }
        return `Code expires in ${this.countdownDisplay}`;
    }

    /** Remaining validity as mm:ss (e.g. "9:47"). */
    get countdownDisplay(): string {
        const total = Math.max(0, this.secondsRemaining);
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    onCodeChange(code: string): void {
        this.code = code ?? '';
    }

    submit(code: string = this.code) {
        if (code.length !== 6) {
            return;
        }

        this.isSubmitting = true;

        const onSuccess = (response: TwoFactorVerificationResponse) => {
            this.isSubmitting = false;
            this.isSuccess = true;
            this._successTimer = setTimeout(() => {
                // Annotated const rather than a cast, so excess-property checking applies.
                const result: ConfirmTwoFactorOutput = {
                    success: true,
                    type: this.type,
                    token: response.verificationKey,
                    trustDevice: this.trustDevice,
                };
                this.closed.emit(result);
            }, 800);
        };

        const onError = () => {
            this.isSubmitting = false;
            this.triggerError();
        };

        if (this.type === 'google') {
            this._userSettingsService.verifyGoogleAuthCode(code, this.data.userId, this.trustDevice)
                .pipe(takeUntil(this._session$), untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        } else if (this.type === 'sms') {
            this._userSettingsService.verifySmsCode(code, this.data.userId, this.trustDevice)
                .pipe(takeUntil(this._session$), untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        }
    }

    /** Redeem a recovery (backup) code. On success it threads back as type 'recovery' so the
     *  login/reset caller replays it to the gate exactly like a google/sms verification key. */
    submitRecovery() {
        const code = this.recoveryCode.trim();
        if (code.length < 10 || this.isSubmitting || this.isSuccess) {
            return;
        }

        this.isSubmitting = true;

        this._userSettingsService.validateRecoveryCode(code, this.data.userId, this.trustDevice, this.data.recoveryPurpose ?? null)
            .pipe(takeUntil(this._session$), untilDestroyed(this))
            .subscribe({
                next: (response: TwoFactorVerificationResponse) => {
                    this.isSubmitting = false;
                    // Annotated const rather than a cast, so excess-property checking applies.
                    const result: ConfirmTwoFactorOutput = {
                        success: true,
                        type: 'recovery',
                        token: response.verificationKey,
                        trustDevice: this.trustDevice,
                    };

                    if (response.replacementRecoveryCode) {
                        // SD-11060: show the replacement before handing the result back.
                        this.replacementCode = response.replacementRecoveryCode;
                        this._pendingRecoveryResult = result;
                        return;
                    }

                    this.isSuccess = true;
                    this._successTimer = setTimeout(() => this.closed.emit(result), 800);
                },
                error: () => {
                    this.isSubmitting = false;
                    this.triggerRecoveryError();
                },
            });
    }

    /** SD-11060: the user saved the replacement code; finish the verification it was holding. */
    continueAfterReplacement(): void {
        if (!this.replacementSaved || !this._pendingRecoveryResult) {
            return;
        }

        const result = this._pendingRecoveryResult;
        this._pendingRecoveryResult = null;
        this.closed.emit(result);
    }

    ngOnDestroy(): void {
        this.teardown();
    }

    private start(): void {
        this.teardown();
        this.isSubmitting = false;
        this.isSuccess = false;
        this.canGoBack = true;
        this.recoveryCode = '';
        this.replacementCode = null;
        this.replacementSaved = false;
        this.replacementCopyState = null;
        this._pendingRecoveryResult = null;
        this._previousType = null;
        this.isSendingSms = false;
        this.isResending = false;
        this.trustDevice = false;
        this.hasError = false;
        this.code = '';

        this.type = this.data.type;

        if (this.type === 'sms') {
            this.setSmsType();
        }

        if (this.data.hasOwnProperty('canGoBack')) {
            this.canGoBack = this.data.canGoBack;
        }

        this.showTrustDevice = !!this.data.showTrustDevice;

        // Explicit !== false, NOT a truthy check: undefined means "this caller doesn't know", which
        // must keep the link (pre-auth gates). Coercing it would silently strip the fallback from
        // every login challenge.
        this.canUseRecoveryCode = this.data.hasRecoveryCodes !== false;
    }

    private teardown(): void {
        this._session$.next();
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
        this._errorTimer = null;
        this._successTimer = null;
        this.stopCountdown();
    }

    private focusDigits(): void {
        this._digits?.nativeElement.shadowRoot?.querySelector('input')?.focus();
    }

    private triggerError() {
        this.hasError = true;
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
        }, 600);
    }

    private triggerRecoveryError() {
        this.hasError = true;
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.recoveryCode = '';
        }, 600);
    }

    /** (Re)start the validity countdown from the full window. Cancels any prior countdown. */
    private startCountdown() {
        this.stopCountdown();
        this.isExpired = false;
        this.smsSendFailed = false;
        this.secondsRemaining = this._codeValiditySeconds;

        this._countdownSub = timer(1000, 1000)
            .pipe(
                take(this._codeValiditySeconds),
                map((elapsed) => this._codeValiditySeconds - (elapsed + 1)),
                untilDestroyed(this),
            )
            .subscribe((remaining) => {
                this.secondsRemaining = remaining;
                if (remaining <= 0) {
                    this.isExpired = true;
                }
            });
    }

    private stopCountdown() {
        this._countdownSub?.unsubscribe();
        this._countdownSub = null;
        this.secondsRemaining = 0;
        this.isExpired = false;
        this.smsSendFailed = false;
    }
}
